import { Directive, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormGroup, ValidationErrors } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject, of } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { Command, CommandBase, standardCommand } from '../../model/command';
import { HelpTextDictionary } from '../../model/help-text';
import { MenuItemDefinition } from '../../model/menu-item-definition';
import { isReplicable } from '../../model/replicable';
import { CommandDispatcher } from '../../service/command/command-dispatcher.service';
import { CommandHandlerResolver } from '../../service/command/command-handler-resolver.service';
import { NavigationService } from '../../service/navigation.service';
import { CanComponentDeactivate } from '../../service/pending-changes.service';
import { CommandController } from '../base/command-controller.base';
import { ModelBase } from './../../model/model-base';
import { DataService, IModel } from './../../service/base/data.service';
import { MessageService } from './../../service/message.service';
import { FormControlEx } from './form-control';

// See "form-component.base2.ts" for a modern implementation that has the service as the first argument instead.
/** Legacy base implementation - Has the model as first generic argument. */
@Directive()
export abstract class FormComponentBase<
    TModel extends ModelBase,
    TDataService extends DataService<TModel, any, IModel> = DataService<TModel>,
    TModelCreate extends TDataService extends DataService<TModel, infer U, IModel> ? U : any = TDataService extends DataService<
      TModel,
      infer U,
      IModel
    >
      ? U
      : TModel,
    TModelUpdate extends TDataService extends DataService<TModel, TModelCreate, infer U> ? U : IModel = TDataService extends DataService<
      TModel,
      TModelCreate,
      infer U
    >
      ? U
      : TModel
  >
  extends CommandController
  implements OnInit, CanComponentDeactivate, OnDestroy
{
  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    protected readonly dataService: TDataService,
    protected readonly messageService: MessageService,
    protected readonly navigation: NavigationService,
    commandDispatcher: CommandDispatcher,
    handlerResolver: CommandHandlerResolver
  ) {
    super(commandDispatcher, handlerResolver);
  }

  protected commandContext: TModel;
  private _isDestroyed = false;
  dataContext: TModel;
  form: FormGroup;
  helpText: HelpTextDictionary;
  get isNew(): boolean {
    return this.dataContext && this.dataContext.isNew;
  }
  menuDefinition: MenuItemDefinition[] = [];
  setDefaults: boolean = false;
  addDeleteMenu: boolean = true;

  initializedSections: string[] = [];
  dataContextInitialized$: Subject<boolean> = new Subject<boolean>();

  allowSectionInit(section: string) {
    if (!this.isSectionAllowed(section)) {
      this.initializedSections.push(section);
      this.initializeSection(section);
    }
  }

  getControlName(c: AbstractControl): string | null {
    const formGroup = c.parent.controls;
    return Object.keys(formGroup).find(name => c === formGroup[name]) || null;
  }

  protected initializeSection(section: string) {}

  isSectionAllowed(section: string) {
    return this.initializedSections.find(s => s === section);
  }

  private _coerceCommand(command: CommandBase) {
    if (command instanceof Command && !command.context) {
      this.commandContext = this.prepareSave();
      command.context = this.commandContext;
    }
  }

  private _handleError(resp: any) {
    this.formSubmittedWithError();
    if (resp.error) {
      const keys = Object.keys(resp.error);
      keys.forEach(key => {
        if (this.form.contains(key)) {
          this.form.controls[key].setErrors({ key: { message: resp.error[key] } });
          this.form.controls[key].markAsTouched();
        }
      });
      if (this.form.invalid) {
        FormComponentBase.markAsTouched(this.form);
        return;
      }
    }
    this.messageService.showError(resp);
  }

  protected addMenuItemDefinition(caption: string, commandName: string, disableItem?: (context: any) => boolean): void {
    if (!this.menuDefinition.find(item => item.commandId == commandName))
      this.menuDefinition.push(new MenuItemDefinition(caption, commandName, null, disableItem));
  }

  protected abstract createModel(): TModel;

  protected executedWithResult(result: boolean, command: CommandBase): void {
    if (result && this.commandContext) {
      Object.assign(this.dataContext, this.commandContext);
      Object.keys(this.dataContext).map(key => {
        if (this.form.contains(key)) {
          this.form.controls[key].setValue(this.dataContext[key]);
        }
      });
      this.commandContext = null;
    }
  }

  protected formSubmitted() {
    this._saving = false;
    this.form.markAsPristine();
  }

  protected formSubmittedNew(id: string) {
    this.formSubmitted();
  }

  protected formSubmittedChange(response?: any) {
    this.formSubmitted();
  }

  protected formSubmittedWithError(): void {
    this._saving = false;
  }

  protected abstract getFormGroup(): FormGroup;

  protected getHelpTextDictionary(): HelpTextDictionary {
    return null;
  }

  newGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0,
        v = c == 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /** Optional generic alternative to "prepareSave" method, specifically for creating new entities. */
  protected prepareCreate?(): TModelCreate;

  /** Optional generic alternative to "prepareSave" method, specifically for updating existing entities. */
  protected prepareUpdate?(): TModelUpdate;

  /** Legacy saving method for BOTH creating and updating entities. */
  protected prepareSave(): TModel {
    let model = this.createModel();
    Object.assign(model, this.form.value);

    // Remove data that should not be submitted:
    for (const p in this.form.controls) {
      const control = this.form.controls[p];
      if ((control as FormControlEx)?.noSubmit) {
        delete model[p];
      }
    }

    model.id = this.dataContext.id;
    return model;
  }

  protected init(data: TModel) {
    if (data) {
      this.dataContext = data;
      this.form = this.getFormGroup();
      this.dataContextInitialized$.next(true);
    }
  }

  cancel() {
    this.navigation.navigateToList();
  }

  innerFormDirty(): boolean {
    return false;
  }

  private isDirty() {
    return this.form?.dirty || this.innerFormDirty();
  }

  canDeactivate(): boolean | Observable<boolean> | Promise<boolean> {
    if (!this.isDirty()) {
      this.onDeactivate();
      return true;
    }
    return this.messageService.showDialog('PENDING_CHANGES_TEXT').pipe(
      tap(deactivate => {
        if (deactivate) {
          this.onDeactivate();
        }
      })
    );
  }

  onDeactivate(): void {
    if (this.dataContext && !this.dataContext.isNew) {
      // Storing RecentId on deactivation ensures these two scenarios work correctly:
      // 1) custom navigation calls in our code, and 2) browser page refresh.
      this.dataContext.storeRecentId({ isDeactivation: true });
    }
  }

  canExecute(command: CommandBase): boolean {
    this._coerceCommand(command);
    return super.canExecute(command);
  }

  execute(command: CommandBase): void {
    if (this._isDestroyed) {
      return;
    }

    this._coerceCommand(command);

    if (!this.isDirty()) {
      super.execute(command);
      return;
    }

    this.AddSubscription(
      this.messageService
        .showDialog('PENDING_CHANGES_TEXT_ACTION')
        .pipe(take(1))
        .subscribe({
          next: dialogResult => {
            if (dialogResult) {
              super.execute(command);
              this.form.markAsPristine(); // TODO: only if command.id === "create" ...maybe?? (would be nice if we had command.isNavigation)
            }
          },
          error: error => {
            this.messageService.showError(error);
          },
        })
    );
  }

  ngOnInit() {
    this.helpText = this.getHelpTextDictionary();
    if (this.addDeleteMenu) this.addMenuItemDefinition('DELETE_BTN', standardCommand.delete);
    let isTemplate = false;

    this.AddSubscription(
      this._activatedRoute.paramMap
        .pipe(
          switchMap(map => {
            let id = map.get('id');
            if (id) {
              return this.dataService.get(id);
            }
            let templateId = map.get('templateId');

            if (templateId) {
              isTemplate = true;
              return this.dataService.get(templateId);
            }

            if (this.setDefaults) {
              return this.dataService.getDefaults();
            }

            return of(this.createModel());
          })
        )
        .subscribe(data => {
          if (isTemplate && isReplicable(data)) {
            const result = data.replicate();
            return this.init(result);
          }

          return this.init(data);
        })
    );
  }

  ngOnDestroy(): void {
    this._isDestroyed = true;
    super.ngOnDestroy();
  }

  updateValueAndValidityRecursive(control: AbstractControl, emitEvent: boolean) {
    if (control instanceof FormGroup || control instanceof FormArray) {
      for (let i in control.controls) this.updateValueAndValidityRecursive(control.controls[i], emitEvent);
    } else control.updateValueAndValidity({ emitEvent: emitEvent });
  }

  getFormValidationErrors(control: AbstractControl): { control: FormControlEx; error: string; value: any }[] {
    let result: { control: FormControlEx; error: string; value: any }[] = [];

    if (control instanceof FormGroup || control instanceof FormArray) {
      for (let i in control.controls) {
        let errors = this.getFormValidationErrors(control.controls[i]);
        if (errors) result = result.concat(errors);
      }
      return result;
    }

    if (!(control instanceof FormControlEx)) return result;

    const controlErrors: ValidationErrors = control.errors;

    if (controlErrors) {
      Object.keys(controlErrors).forEach(keyError => {
        result.push({
          control: control,
          error: keyError,
          value: controlErrors[keyError],
        });
      });
    }

    return result;
  }

  public static markAsTouched(control: AbstractControl) {
    if (control instanceof FormGroup || control instanceof FormArray) {
      for (let i in control.controls) {
        this.markAsTouched(control.controls[i]);
      }
    } else control.markAsTouched();
  }

  private _saving = false;
  public get isSaving() {
    return this._saving;
  }

  onSubmit() {
    if (this._saving || !this.form) return;

    if (this.form.invalid) {
      FormComponentBase.markAsTouched(this.form);
      this.showPageValidationError();
    } else if (!this.isDirty()) {
      return;
    } else if (this.form.valid) {
      this._saving = true;
      if (this.isNew) {
        if (this.prepareCreate && this.dataService.createNew) {
          // modern flow using TModelCreate
          const model = this.prepareCreate();
          this.AddSubscription(
            this.dataService.createNew(model).subscribe({
              next: m => {
                this.formSubmittedNew(m.id);
                this.dataContext.id = m.id;
                this.dataContext.storeRecentId(); // store the new id so it can be highlighted in the list
                this.navigateAfterSave(m.id);
              },
              error: error => this._handleError(error),
            })
          );
        } else {
          // legacy code
          const model = this.prepareSave();
          this.AddSubscription(
            this.dataService.add(model).subscribe({
              next: id => {
                this.formSubmittedNew(id);
                this.dataContext.id = id;
                this.dataContext.storeRecentId(); // store the new id so it can be highlighted in the list
                this.navigateAfterSave(id);
              },
              error: error => this._handleError(error),
            })
          );
        }
      } else {
        if (this.prepareUpdate && this.dataService.updateExisting) {
          // modern flow using TModelUpdate
          const model = this.prepareUpdate();
          this.AddSubscription(
            this.dataService.updateExisting(model).subscribe({
              next: response => this.formSubmittedChange(response),
              error: error => this._handleError(error),
            })
          );
        } else {
          // legacy code
          const model = this.prepareSave();
          this.AddSubscription(
            this.dataService.change(model).subscribe({
              next: response => this.formSubmittedChange(response),
              error: error => this._handleError(error),
            })
          );
        }
      }
    }
  }

  protected showPageValidationError() {
    let error = this.getFormValidationErrors(this.form).find(e => e.control.getErrorMessage() && e.control.caption);
    if (error) {
      this.messageService.showError('{key}: {value}', { key: error.control.caption, value: error.control.getErrorMessage() });
    }
  }

  protected navigateAfterSave(id: any) {
    this.navigation.navigateToForm(id);
  }
}
